<template>
  <div class="container-contact-inquiry">
    <div class="contact-item">
      <div class="top flex-btw">
        <p class="primary">{{ t(item.title) }}</p>
        <div
          :class="{
            'pc-badge': true,
            success: item.answerStatus === 'ANSWERED',
            gray: item.answerStatus === 'WAITING_ANSWER',
          }"
        >
          {{ getInquiryStatus(item.answerStatus) }}
        </div>
      </div>
      <div class="my-q mb-4">
        {{ item.content }}
      </div>
      <div class="bottom">
        <p class="dim">{{ t('inquiry_created_at') }} : {{ dateFormateKebabTimestamp(item.createdAt) }}</p>
        <button
          v-if="item.answerStatus === 'ANSWERED' && inquiryAnswers.length === 0"
          type="button"
          @click="requestInquiryAnswers"
        >
          {{ t('ss_inquiry_button_get_answer') }}
        </button>
      </div>
    </div>
    <div v-if="item.answerStatus === 'ANSWERED' && inquiryAnswers.length > 0" class="answer-area">
      <p class="bold">{{ t('ss_list_inquiry_answer') }} ({{ inquiryAnswers.length }})</p>
      <ul class="answer-list">
        <template v-for="answer in inquiryAnswers" :key="answer.id">
          <li v-if="answer.type === 'REINQUIRY'">
            <div class="tit-flex">
              <div class="point">[{{ t('ss_inquiry_reinquiry') }}]</div>
              <p>{{ answer.content }}</p>
            </div>
            <p class="dim">{{ t('label_inquiry_date') }} : {{ dateFormateKebabTimestamp(answer.createdAt) }}</p>
          </li>
          <li v-else class="re">
            <div class="tit-flex">
              <div class="point">
                <img :src="getAssetImageUrl('common/icons/indent.svg')" alt="" />
                RE:
              </div>
              <p>{{ answer.content }}</p>
            </div>
            <p class="dim">{{ t('label_inquiry_date') }} : {{ dateFormateKebabTimestamp(answer.createdAt) }}</p>
          </li>
        </template>
      </ul>
      <template v-if="isInquiryEnabled">
        <textarea v-model="inquiryContent" class="mt-3" rows="6" id="inquiry_content" />
        <div class="btn-pst mt-3">
          <button type="button" class="bk-btn m-btn" @click="requestReInquiry">
            {{ t('label_resend') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchSelfServiceInquiryResponse, addSelfServiceInquiryFollowup } from '@/api/apiService';
import { useToastStore } from '@/stores/toast';
import { IResponseSelfServiceInquiry, IResponseSelfServiceAnswer } from '@/types/response';
import { dateFormateKebabTimestamp } from '@/utils/dateFormatter.js';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const props = withDefaults(
  defineProps<{
    item: IResponseSelfServiceInquiry;
  }>(),
  {
    item: () => ({}) as IResponseSelfServiceInquiry,
  }
);

const emit = defineEmits<{ search: [] }>();

const toastStore = useToastStore();
const { t } = useI18n();

const inquiryAnswers = ref<IResponseSelfServiceAnswer[]>([]);
const inquiryContent = ref<string>('');

const isInquiryEnabled = computed<boolean>(() => {
  return inquiryAnswers.value.length > 0 && inquiryAnswers.value.at(-1)!.type === 'ANSWER';
});

const getInquiryStatus = (status: string): string => {
  return (
    {
      WAITING_ANSWER: t('inquiry_pending'),
      ANSWERED: t('inquiry_is_answered'),
    }[status] || ''
  );
};

const requestInquiryAnswers = async () => {
  try {
    const { success, data } = await fetchSelfServiceInquiryResponse(props.item.id);
    if (!success) {
      throw new Error();
    }
    inquiryAnswers.value = data;
  } catch {
    inquiryAnswers.value = [];
  }
};

const requestReInquiry = async () => {
  if (inquiryContent.value) {
    try {
      await addSelfServiceInquiryFollowup({ content: inquiryContent.value, inquiryId: props.item.id });
      await requestInquiryAnswers();
      emit('search');
      inquiryContent.value = '';
    } catch {
      toastStore.error('');
    }
  }
};
</script>

<style scoped lang="scss">
.container-contact-inquiry {
  &.active {
    padding: 1.5rem 0 0;
  }
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid $color-gray-40;

  .contact-item {
    .top {
      padding-bottom: $size-xs;
      .primary {
        font-weight: 600;
      }
    }
    .bottom {
      display: flex;
      justify-content: space-between;
    }
  }

  .answer-area {
    background: $gray5;
    border-top: 1px solid $color-gray-40;
    padding: 1rem;
    > .bold {
      font-weight: 500;
      font-size: $font-size-psmall;
      color: $color-navy-100;
    }
    .answer-list {
      padding-top: $size-m;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      li {
        &:not(:first-child) {
          border-top: 1px dashed $color-gray-40;
          padding-top: 1rem;
        }
        .tit-flex {
          display: flex;
          align-items: center;
          gap: $size-s;
          padding-bottom: $size-s;
          .point {
            font-weight: 600;
          }
        }
        &.re {
          .point {
            padding-left: 4px;
            img {
              vertical-align: 3px;
            }
          }
        }
      }
    }
    .btn-pst {
      display: flex;
      justify-content: flex-end;
      button {
        width: auto;
      }
    }
  }

  .dim {
    font-size: $font-size-psmall;
  }
}
</style>
